import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const steps = ['이메일 입력', '인증 코드 확인', '회원 정보 입력'];

// FingerprintJS를 사용하여 디바이스 고유 ID 생성
const generateDeviceId = async (): Promise<string> => {
  // 로컬 스토리지에서 기존 디바이스 ID 확인
  let deviceId = localStorage.getItem('device_id');
  
  // 없으면 FingerprintJS를 사용하여 새로 생성
  if (!deviceId) {
    try {
      // FingerprintJS 인스턴스 로드
      const fpPromise = FingerprintJS.load();
      const fp = await fpPromise;
      
      // 방문자 식별자 생성
      const result = await fp.get();
      
      // 최종 디바이스 ID 생성 (PC- 접두사 추가)
      deviceId = `PC-${result.visitorId}`;
      
      // 로컬 스토리지에 저장
      localStorage.setItem('device_id', deviceId);
      
      console.log('새 디바이스 ID 생성:', deviceId);
    } catch (error) {
      console.error('디바이스 ID 생성 오류:', error);
      
      // 오류 발생 시 대체 ID 생성
      const fallbackId = `PC-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('device_id', fallbackId);
      deviceId = fallbackId;
    }
  }
  
  return deviceId;
};

const SignUpScreen: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, sendVerificationCode, checkVerificationCode, isLoading } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [formData, setFormData] = useState({
    email: '',           // mail_address와 동일
    verificationCode: '', // check_number와 동일
    name: '',            // login_name과 동일
    loginId: '',         // login_id와 동일
    password: '',
    confirmPassword: ''
  });

  // 컴포넌트 마운트 시 디바이스 ID 생성
  useEffect(() => {
    const initDeviceId = async () => {
      const id = await generateDeviceId();
      setDeviceId(id);
    };
    
    initDeviceId();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendVerification = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('올바른 이메일 주소를 입력해주세요.');
      return;
    }

    try {
      setError('');

      // 이메일 주소로 인증 코드 전송
      await sendVerificationCode(formData.email);
      setActiveStep(1);

    } catch (error) {
      // 서버에서 보내온 오류 메시지 표시
      if (error instanceof Error) {
        setError(error.message || '인증 코드 전송에 실패했습니다.');
      } else {
        setError('인증 코드 전송에 실패했습니다.');
      }      
      console.error('인증 코드 전송 오류:', error);
    }
  };

  const handleVerifyCode = async () => {
    if (!formData.verificationCode) {
      setError('인증 코드를 입력해주세요.');
      return;
    }

    try {
      setError('');

      // 이메일과 인증 코드(check_number)로 확인
      const success = await checkVerificationCode(formData.email, formData.verificationCode);
      if (!success) {
        setError('인증 코드 오류 입니다다.');
        return;
      }

      setActiveStep(2);

    } catch (error) {
      // 서버에서 보내온 오류 메시지 표시
      if (error instanceof Error) {
        setError(error.message || '인증 코드가 올바르지 않습니다.');
      } else {
        setError('인증 코드가 올바르지 않습니다.');
      }
      console.error('인증 코드 확인 오류:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!formData.loginId) {
      setError('아이디를 입력해주세요.');
      return;
    }

    if (!deviceId) {
      setError('디바이스 ID를 생성할 수 없습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    try {
      setError('');
      
      const guestToken = localStorage.getItem('guestToken');
      if (!guestToken) return;

      // 회원가입 API 호출
      // verificationCode는 check_number와 동일
      // email은 mail_address와 동일
      await signUp(guestToken, {
        login_name: formData.name,
        password: formData.password,
        check_number: formData.verificationCode, // 인증 코드 (check_number와 동일)
        mail_address: formData.email, // 이메일 (mail_address와 동일)
        login_id: formData.loginId,
        device_type: 'W', // 웹 환경은 'W'로 고정
        device_id: deviceId // FingerprintJS로 생성된 디바이스 ID 사용
      });
      
      navigate('/login');
    } catch (error) {
      // 서버에서 보내온 오류 메시지 표시
      if (error instanceof Error) {
        setError(error.message || '회원가입에 실패했습니다.');
      } else {
        setError('회원가입에 실패했습니다.');
      }
      console.error('회원가입 오류:', error);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              label="이메일"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSendVerification}
              disabled={isLoading || !formData.email}
              sx={{ mt: 3 }}
            >
              {isLoading ? '전송중...' : '인증 코드 전송'}
            </Button>
          </>
        );
      case 1:
        return (
          <>
            <TextField
              fullWidth
              label="인증 코드"
              name="verificationCode"
              value={formData.verificationCode}
              onChange={handleChange}
              margin="normal"
              required
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleVerifyCode}
              disabled={isLoading || !formData.verificationCode}
              sx={{ mt: 3 }}
            >
              {isLoading ? '확인중...' : '확인'}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => setActiveStep(0)}
              sx={{ mt: 1 }}
            >
              이메일 다시 입력
            </Button>
          </>
        );
      case 2:
        return (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="아이디"
              name="loginId"
              value={formData.loginId}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="이름"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="비밀번호"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="비밀번호 확인"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
              error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
              helperText={
                formData.password !== formData.confirmPassword && formData.confirmPassword !== ''
                  ? '비밀번호가 일치하지 않습니다.'
                  : ''
              }
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading}
              sx={{ mt: 3 }}
            >
              {isLoading ? '가입중...' : '가입하기'}
            </Button>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 3
        }}
      >
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            회원가입
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              이미 계정이 있으신가요?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
              >
                로그인
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SignUpScreen; 